import React, { useMemo, useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// ===== FONT (style attributor) =====
const fontWhitelist = [
  'Arial',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Comic Sans MS',
];

const FontAttributor = Quill.import('attributors/style/font');
FontAttributor.whitelist = fontWhitelist;
Quill.register(FontAttributor, true);

// ===== SIZE (style attributor dengan angka) =====
const sizeWhitelist = [
  '8px',
  '9px',
  '10px',
  '11px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '26px',
  '28px',
  '36px',
  '48px',
  '72px',
];

const SizeAttributor = Quill.import('attributors/style/size');
SizeAttributor.whitelist = sizeWhitelist;
Quill.register(SizeAttributor, true);

const AlignAttributor = Quill.import('attributors/style/align');
Quill.register(AlignAttributor, true);

interface EmailEditorProps {
  value: string;
  onChange: (data: string) => void;
}

const EmailEditor = ({ value, onChange }: EmailEditorProps) => {
  const quillRef = useRef<ReactQuill | null>(null);

  // Default: Arial, 14px - maintain format at all times
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // Set initial format
    editor.format('font', 'Arial');
    editor.format('size', '14px');

    // Ensure format is maintained on every text change
    const handleTextChange = () => {
      const selection = editor.getSelection();
      if (!selection) return;

      const format = editor.getFormat(selection.index, selection.length);
      
      // If text is a heading, remove font-size to use default heading size
      if (format.header) {
        // Remove size attribute for headings
        editor.formatText(selection.index, selection.length || 1, {
          size: false
        });
        // Apply font if missing
        if (!format.font) {
          editor.formatText(selection.index, selection.length || 1, {
            font: 'Arial'
          });
        }
      } else {
        // For normal text (non-heading), apply default font and size if missing
        if (!format.font || !format.size) {
          editor.formatText(selection.index, selection.length || 1, {
            font: 'Arial',
            size: '14px'
          });
        }
      }
    };

    // Update header picker to show active state
    const updateHeaderPicker = () => {
      const range = editor.getSelection();
      if (!range) return;

      const format = editor.getFormat(range);
      const headerPicker = document.querySelector('.ql-header .ql-picker-label') as HTMLElement;
      
      if (headerPicker) {
        if (format.header) {
          headerPicker.setAttribute('data-value', String(format.header));
        } else {
          // Set to false to show "Normal" as active (blue)
          headerPicker.setAttribute('data-value', 'false');
        }
      }
    };

    // Listen to selection changes to apply format when cursor moves to unformatted text
    const handleSelectionChange = (range: any) => {
      if (!range) return;
      
      const format = editor.getFormat(range.index, range.length);
      
      // If cursor is in a heading, remove font-size
      if (format.header) {
        editor.format('size', false);
        // Apply font if missing
        if (!format.font) {
          editor.format('font', 'Arial');
        }
      } else {
        // For normal text, apply default format if missing
        if (!format.font) {
          editor.format('font', 'Arial');
        }
        if (!format.size) {
          editor.format('size', '14px');
        }
      }

      // Update header picker
      updateHeaderPicker();
    };

    editor.on('text-change', handleTextChange);
    editor.on('selection-change', handleSelectionChange);

    // Initial update
    updateHeaderPicker();

    return () => {
      editor.off('text-change', handleTextChange);
      editor.off('selection-change', handleSelectionChange);
    };
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          [{ font: fontWhitelist }],
          [{ size: sizeWhitelist }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          ['clean'],
        ],
        handlers: {
          header: function(value: any) {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;
            
            const range = editor.getSelection();
            if (!range) return;
            
            // Apply header format
            editor.format('header', value);
            
            // If changing to heading (h1, h2, h3), remove font-size
            if (value) {
              editor.formatText(range.index, range.length || 1, {
                size: false
              });
            } else {
              // If changing back to normal, apply default size
              editor.formatText(range.index, range.length || 1, {
                size: '14px'
              });
            }
          }
        }
      }
    }),
    []
  );

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'bullet',
    'link',
  ];

  return (
    <>
      <style>{`
        /* ===== FONT DROPDOWN LABEL + PREVIEW ===== */
        .ql-toolbar.ql-snow .ql-picker.ql-font {
          min-width: 150px;      /* bisa dinaikkan lagi kalau masih mepet, misal 160/170 */
}
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Arial"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Arial"]::before {
          content: 'Arial';
          font-family: Arial, Helvetica, sans-serif;
        }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Times New Roman"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Times New Roman"]::before {
          content: 'Times New Roman';
          font-family: 'Times New Roman', Times, serif;
        }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Courier New"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Courier New"]::before {
          content: 'Courier New';
          font-family: 'Courier New', Courier, monospace;
        }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Georgia"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Georgia"]::before {
          content: 'Georgia';
          font-family: Georgia, serif;
        }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Verdana"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Verdana"]::before {
          content: 'Verdana';
          font-family: Verdana, Geneva, sans-serif;
        }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Tahoma"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Tahoma"]::before {
          content: 'Tahoma';
          font-family: Tahoma, Geneva, sans-serif;
        }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Trebuchet MS"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Trebuchet MS"]::before {
          content: 'Trebuchet MS';
          font-family: 'Trebuchet MS', Helvetica, sans-serif;
        }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Comic Sans MS"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Comic Sans MS"]::before {
          content: 'Comic Sans MS';
          font-family: 'Comic Sans MS', cursive, sans-serif;
        }

        /* Font label 1 baris + ellipsis kalau kepanjangan */
        .ql-snow .ql-picker.ql-font .ql-picker-label::before,
        .ql-snow .ql-picker.ql-font .ql-picker-item::before {
          display: inline-block;
          max-width: 150px;   /* bisa disesuaikan */
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        /* ===== SIZE DROPDOWN: angka saja, item size 14px ===== */
        .ql-toolbar.ql-snow .ql-picker.ql-size {
          width: 60px;           /* cukup buat 2 digit (8–72) */
        }
          
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="8px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="8px"]::before {
          content: '8';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="9px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="9px"]::before {
          content: '9';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before {
          content: '10';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before {
          content: '11';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before {
          content: '12';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before {
          content: '14';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before {
          content: '16';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before {
          content: '18';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before {
          content: '20';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="22px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="22px"]::before {
          content: '22';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before {
          content: '24';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="26px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="26px"]::before {
          content: '26';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="28px"]::before {
          content: '28';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before {
          content: '36';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before {
          content: '48';
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="72px"]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="72px"]::before {
          content: '72';
        }

        /* Semua item & label size tampil dengan font 14px */
        .ql-snow .ql-picker.ql-size .ql-picker-item::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label::before {
          font-size: 14px;
        }

        /* Force header picker to show blue when Normal is active */
        .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="false"]::before {
          content: 'Normal';
        }
        
        .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="false"] {
          color: #06c !important;
        }

        /* Heading styles in editor */
        .ql-editor h1 {
          font-size: 2rem !important;
          font-weight: 700 !important;
          line-height: 1.2 !important;
          margin: 0.5rem 0 !important;
        }

        .ql-editor h2 {
          font-size: 1.5rem !important;
          font-weight: 600 !important;
          line-height: 1.3 !important;
          margin: 0.5rem 0 !important;
        }

        .ql-editor h3 {
          font-size: 1.17rem !important;
          font-weight: 600 !important;
          line-height: 1.4 !important;
          margin: 0.5rem 0 !important;
          color: #374151 !important;
        }

        /* Fix link tooltip positioning - prevent collision with left sidebar */
        .ql-snow .ql-tooltip {
          left: 0 !important;
          transform: translateX(0) !important;
          margin-left: 0 !important;
        }

        .ql-snow .ql-tooltip[data-mode="link"]::before {
          content: "Enter link:";
        }

        .ql-snow .ql-tooltip.ql-editing {
          left: 0 !important;
        }
      `}</style>

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        style={{ height: '400px', marginBottom: '70px' }}
      />
    </>
  );
};

export default EmailEditor;
